import {ChangeDetectionStrategy, Component, OnDestroy, ViewEncapsulation, input, signal} from '@angular/core';
import Cropper from 'cropperjs';

/**
 * An image with cropperjs on it, as angular-cropperjs's `<angular-cropper>` drew it: a spinner until
 * cropperjs is ready, then the crop box. `cropper` is the cropperjs instance, once the image has
 * loaded. Not encapsulated, like the component it replaces, so cropperjs's stylesheet reaches the
 * elements cropperjs adds.
 */
@Component({
  selector: 'app-image-cropper',
  template: `
    <div class="cropper-wrapper">
      @if (isLoading()) {
        <div class="loading-block"><div class="spinner"></div></div>
      }
      @if (loadError()) {
        <div class="alert alert-warning"></div>
      }
      <div class="cropper">
        <img alt="image" [src]="imageUrl()" (load)="imageLoaded($event)" (error)="imageLoadError()" />
      </div>
    </div>
  `,
  styleUrls: ['./image-cropper.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageCropperComponent implements OnDestroy {
  readonly imageUrl = input<string>(undefined);
  readonly cropperOptions = input<Cropper.Options>({});

  cropper: Cropper | null = null;
  readonly isLoading = signal(true);
  readonly loadError = signal(false);

  ngOnDestroy(): void {
    this.cropper?.destroy();
    this.cropper = null;
  }

  imageLoaded(event: Event): void {
    this.loadError.set(false);
    const image = event.target as HTMLImageElement;
    const options = {aspectRatio: NaN, checkCrossOrigin: true, ...this.cropperOptions()};
    if (options.checkCrossOrigin) {
      image.crossOrigin = 'anonymous';
    }
    image.addEventListener('ready', () => {
      // cropperjs's own event, after the load: a signal, so the spinner goes.
      this.isLoading.set(false);
    });
    this.cropper?.destroy();
    this.cropper = new Cropper(image, options);
  }

  imageLoadError(): void {
    this.loadError.set(true);
    this.isLoading.set(false);
  }
}
